import { useState } from 'react'
import { useIntl } from 'react-intl'
import styled from 'styled-components'

import Flex from 'shared/ui/Flex'
import Button from 'shared/ui/Button_2'
import Icon from 'shared/ui/Icon'
import IslandModal from 'shared/components/modals/IslandModal'
import { ProjectExportTypes } from '../../types'
import fileAttachmentIconSrc from 'screens/ProjectExport/components/ListItem/images/file-attachment-02.svg'

const LabelBox = styled.label<{ isSelected: boolean }>`
  display: flex;
  flex: 1;
  flex-direction: column;
  align-items: center;
  row-gap: 12px;
  padding: 24px 16px;
  box-shadow: 1px 2px 6px 0 ${({ theme }) => theme.color.base.c2};
  border: 2px solid
    ${({ theme, isSelected }) =>
      isSelected ? theme.color.brand_02 : theme.color.base.c4};
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  color: ${({ theme, isSelected }) =>
    isSelected ? theme.color.brand_02 : theme.color.base.c8};
  cursor: pointer;
  text-align: center;
  transition: all 0.2s ease;
  background-color: ${({ theme, isSelected }) =>
    isSelected ? theme.color.base.c2 : 'transparent'};

  &:hover {
    border-color: ${({ theme }) => theme.color.brand_02};
  }
`

const IconContainer = styled(Flex)<{ isSelected: boolean }>`
  width: 50px;
  height: 50px;
  background-color: ${({ theme, isSelected }) =>
    isSelected ? theme.color.brand_02 : theme.color.base.c3};
  border-radius: 5px;
  align-items: center;
  justify-content: center;
  transition: background-color 0.2s ease;

  & > img {
    display: block;
    width: 33px;
    height: 32px;
    filter: ${({ isSelected }) =>
      isSelected ? 'brightness(0) invert(1)' : 'none'};
  }

  & > svg path {
    fill: ${({ isSelected }) => (isSelected ? '#fff' : '#212529')};
  }
`

const HiddenRadio = styled.input.attrs({ type: 'radio' })`
  display: none;
`

type CoverExportModalProps = {
  isOpen: boolean
  closeModal: () => void
  onConfirm: (outputProfile: ProjectExportTypes) => void
  isLoading?: boolean
}

const CoverExportModal: React.FC<CoverExportModalProps> = ({
  isOpen,
  closeModal,
  onConfirm,
  isLoading = false
}) => {
  const { formatMessage } = useIntl()
  const [selectedType, setSelectedType] = useState<ProjectExportTypes>(
    ProjectExportTypes.preview
  )

  const handleConfirm = () => {
    onConfirm(selectedType)
  }

  return (
    <IslandModal
      isOpen={isOpen}
      closeModal={closeModal}
      width={600}
      title={formatMessage({ id: 'AdminExport.cover.modal.title' })}
      titleAlign="left"
      titleSize={22}
    >
      <Flex direction="column" rowGap={32}>
        <Flex direction="column" rowGap={16}>
          <Flex
            direction="column"
            rowGap={8}
            marginBottom={8}
          >
            <div style={{ fontSize: '16px', fontWeight: 600 }}>
              {formatMessage({ id: 'AdminExport.cover.modal.selectType' })}
            </div>
            <div style={{ fontSize: '14px', color: '#6C757D' }}>
              {formatMessage({ id: 'AdminExport.cover.modal.description' })}
            </div>
          </Flex>

          <Flex columnGap={16}>
            <Flex flex={1} direction="column">
              <HiddenRadio
                id="preview"
                name="exportType"
                value={ProjectExportTypes.preview}
                checked={selectedType === ProjectExportTypes.preview}
                onChange={() => setSelectedType(ProjectExportTypes.preview)}
              />
              <LabelBox
                htmlFor="preview"
                isSelected={selectedType === ProjectExportTypes.preview}
              >
                <IconContainer
                  isSelected={selectedType === ProjectExportTypes.preview}
                >
                  <img src={fileAttachmentIconSrc} alt="Preview" />
                </IconContainer>
                <div>
                  {formatMessage({ id: 'AdminExport.cover.modal.preview' })}
                </div>
              </LabelBox>
            </Flex>

            <Flex flex={1} direction="column">
              <HiddenRadio
                id="print"
                name="exportType"
                value={ProjectExportTypes.print}
                checked={selectedType === ProjectExportTypes.print}
                onChange={() => setSelectedType(ProjectExportTypes.print)}
              />
              <LabelBox
                htmlFor="print"
                isSelected={selectedType === ProjectExportTypes.print}
              >
                <IconContainer
                  isSelected={selectedType === ProjectExportTypes.print}
                >
                  <Icon
                    name="printer"
                    size={33}
                    color={selectedType === ProjectExportTypes.print ? '#fff' : '#212529'}
                  />
                </IconContainer>
                <div>
                  {formatMessage({ id: 'AdminExport.cover.modal.print' })}
                </div>
              </LabelBox>
            </Flex>
          </Flex>
        </Flex>

        <Flex justifyContent="flex-end" columnGap={16}>
          <Button
            intent="secondary-base"
            textId="Add.page.cancel"
            onPress={closeModal}
            isDisabled={isLoading}
          />
          <Button
            intent="primary-brand-02"
            textId="AdminExport.cover.modal.export"
            onPress={handleConfirm}
            loading={isLoading}
            isDisabled={isLoading}
          />
        </Flex>
      </Flex>
    </IslandModal>
  )
}

export default CoverExportModal
